import React, { useEffect, useState } from "react";
import {
  Drawer,
  Typography,
  Divider,
  Box,
  Button,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  makeStyles,
} from "@material-ui/core";
import MuiAlert from "@material-ui/lab/Alert";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import axios from "axios";

/*
 * BCM-234 A7 — "Delete Plant ID <id>" side drawer.
 *
 * Lists one row per CMO that has active products on the plant
 * (CMO | Associated Products | Alternate Plant ID). For each row the admin
 * picks an alternate plant from the network plant list (the same options for
 * every CMO, excluding the plant being deleted). "Confirm changes" is enabled
 * only once every row has an alternate selected. Confirm -> a confirmation
 * dialog (total reassigned count) -> POST deletePlantAndReassignToProducts
 * (one atomic txn that reassigns the products and removes the plant) ->
 * success dialog -> parent refresh + close. Irreversible.
 *
 * Data source: everything the drawer needs is derived from the plantRows
 * prop (the same list shown on the Plant-SLoc tab). No extra API calls on
 * open — `productCountAtVendorLevel` on the deleted plant's row gives the
 * CMO list + counts, and the other plants in plantRows are the alternates.
 */

const useStyles = makeStyles((theme) => ({
  headerCell: {
    fontWeight: 600,
    color: "#333",
    backgroundColor: "#f0f0f0",
    padding: "10px 16px",
    textAlign: "left",
  },
  bodyCell: {
    padding: "10px 16px",
    textAlign: "left",
    verticalAlign: "middle",
  },
  warningHeading: {
    fontWeight: 700,
    fontSize: "1rem",
    color: "#000",
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  warningBox: {
    border: "1px solid #d32f2f",
    borderRadius: 4,
    padding: "12px 16px",
    backgroundColor: "#fff",
  },
  warningText: {
    color: "#d32f2f",
    fontWeight: 700,
    fontSize: "0.875rem",
    lineHeight: 1.5,
  },
  naText: {
    color: "#999",
  },
  noAltText: {
    color: "#b00020",
    fontSize: "0.75rem",
    marginTop: "4px",
  },
  successCheck: {
    color: "#2e7d32",
    fontSize: "1.5rem",
    verticalAlign: "middle",
    marginLeft: theme.spacing(1),
  },
  successHeading: {
    fontWeight: 700,
    fontSize: "1.125rem",
    color: "#000",
    display: "flex",
    alignItems: "center",
  },
  successSubLabel: {
    fontWeight: 700,
    fontSize: "0.95rem",
    color: "#000",
    marginTop: theme.spacing(2),
  },
  successCount: {
    fontSize: "0.95rem",
    color: "#333",
    marginTop: theme.spacing(0.5),
  },
}));

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

const DeletePlantDrawer = ({
  open,
  plantID,
  buildUrl,
  plantRows,
  onClose,
  onDeleted,
}) => {
  const classes = useStyles();

  const [rows, setRows] = useState([]);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [successOpen, setSuccessOpen] = useState(false);
  const [successCount, setSuccessCount] = useState(0);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  // Network alternate-plant options: every plant in plantRows EXCEPT the one
  // being deleted. Each option carries its own storageLocationIDs so we can
  // attach them to the reassignment payload when this option is picked.
  const alternateOptions = (plantRows || [])
    .filter((p) => p && !p.isNew && String(p.plantID) !== String(plantID))
    .map((p) => ({
      plantID: String(p.plantID),
      storageLocationIDs: Array.isArray(p.storageLocationIDs)
        ? [...p.storageLocationIDs]
        : [],
    }));

  // Derive the per-CMO rows from the deleted plant's productCountAtVendorLevel.
  // The Plant-SLoc tab already fetched this; no extra API call here.
  useEffect(() => {
    if (!open || !plantID) {
      setRows([]);
      return;
    }
    const target = (plantRows || []).find(
      (p) => p && String(p.plantID) === String(plantID)
    );
    const cmoList = Array.isArray(target?.productCountAtVendorLevel)
      ? target.productCountAtVendorLevel
      : [];
    setRows(
      cmoList.map((cmo) => ({
        vendorMosymphonyID: cmo.vendorMosymphonyID,
        vendorName: cmo.vendorName,
        count: Number(cmo.productCount || 0),
        alternatePlantID: "",
      }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, plantID, plantRows]);

  const handleAlternateChange = (vendorMosymphonyID, value) => {
    setRows((prev) =>
      prev.map((r) =>
        r.vendorMosymphonyID === vendorMosymphonyID
          ? { ...r, alternatePlantID: value }
          : r
      )
    );
  };

  // Confirm enabled only when every count>0 row has an alternate selected.
  const canConfirm = rows
    .filter((r) => r.count > 0)
    .every((r) => r.alternatePlantID && r.alternatePlantID !== "");

  const totalChanged = rows
    .filter((r) => r.count > 0 && r.alternatePlantID)
    .reduce((sum, r) => sum + (r.count || 0), 0);

  const handleConfirmDelete = async () => {
    setSubmitting(true);
    try {
      // Build the per-CMO reassignment list. For each picked alternate plant we
      // also send that alternate plant's storageLocationIDs (looked up from the
      // network options) so the backend can carry them onto the reassigned products.
      const perCmoAlternates = rows
        .filter((r) => r.count > 0 && r.alternatePlantID)
        .map((r) => {
          const alt = alternateOptions.find(
            (o) => o.plantID === String(r.alternatePlantID)
          );
          return {
            vendorMosymphonyID: r.vendorMosymphonyID,
            alternatePlantID: r.alternatePlantID,
            storageLocationIDs: alt ? [...alt.storageLocationIDs] : [],
          };
        });

      // senderLegalName + per-CMO receiverLegalName are stamped by the middleware
      // from the auth token. FE only sends the business payload.
      const res = await axios.post(
        buildUrl("deletePlantAndReassignToProducts"),
        { plantID, perCmoAlternates }
      );

      const ok = res?.data?.status === true || res?.data?.status === "true";
      if (ok) {
        setConfirmOpen(false);
        setSuccessCount(
          res?.data?.reassignedCount != null
            ? res.data.reassignedCount
            : totalChanged
        );
        setSuccessOpen(true);
      } else {
        setConfirmOpen(false);
        setSnackbar({
          open: true,
          severity: "error",
          message:
            res?.data?.errorMessage ||
            res?.data?.message ||
            "Failed to delete the plant. Please try again.",
        });
      }
    } catch (err) {
      setConfirmOpen(false);
      setSnackbar({
        open: true,
        severity: "error",
        message:
          err?.response?.data?.errorMessage ||
          err?.response?.data?.message ||
          "Failed to delete the plant. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSuccessOk = () => {
    setSuccessOpen(false);
    if (typeof onDeleted === "function") onDeleted();
    if (typeof onClose === "function") onClose();
  };

  return (
    <>
      <Drawer anchor="right" open={open} onClose={onClose}>
        <div
          style={{
            width: 640,
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div style={{ padding: "16px 24px" }}>
            <Typography variant="h6" style={{ fontWeight: 700 }}>
              Delete Plant ID {plantID}
            </Typography>
            <Divider style={{ marginTop: 8 }} />
            <Typography className={classes.warningHeading}>
              Warning:
            </Typography>
            <div className={classes.warningBox}>
              <Typography className={classes.warningText}>
                Plant ID deletion: You can&apos;t reverse this process.
              </Typography>
              <Typography className={classes.warningText}>
                To proceed, please assign alternate Plant ID for all the
                associated products.
              </Typography>
            </div>
          </div>

          <div style={{ flex: 1, padding: "16px 24px", overflow: "auto" }}>
            <Typography
              variant="subtitle1"
              style={{ fontWeight: 600, marginBottom: 12 }}
            >
              Assign Alternate Plant ID
            </Typography>

            {rows.length === 0 ? (
              <Typography style={{ color: "#666" }}>
                No CMOs have active products on this plant. You can delete it directly.
              </Typography>
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.headerCell}>CMO</TableCell>
                    <TableCell className={classes.headerCell}>
                      Associated Products
                    </TableCell>
                    <TableCell className={classes.headerCell}>
                      Alternate Plant ID
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.vendorMosymphonyID}>
                      <TableCell className={classes.bodyCell}>
                        {row.vendorName || `CMO ${row.vendorMosymphonyID}`}
                      </TableCell>
                      <TableCell className={classes.bodyCell}>
                        {`${row.count} Products`}
                      </TableCell>
                      <TableCell className={classes.bodyCell}>
                        {row.count > 0 ? (
                          <>
                            <Select
                              displayEmpty
                              value={row.alternatePlantID}
                              onChange={(e) =>
                                handleAlternateChange(
                                  row.vendorMosymphonyID,
                                  e.target.value
                                )
                              }
                              style={{ minWidth: 200 }}
                              variant="outlined"
                              disabled={alternateOptions.length === 0}
                            >
                              <MenuItem value="" disabled>
                                Select an alternate plant
                              </MenuItem>
                              {alternateOptions.map((opt) => (
                                <MenuItem key={opt.plantID} value={opt.plantID}>
                                  {opt.plantID}
                                </MenuItem>
                              ))}
                            </Select>
                            {alternateOptions.length === 0 && (
                              <div className={classes.noAltText}>
                                No alternate plant available in the network.
                                Add another Plant ID first.
                              </div>
                            )}
                          </>
                        ) : (
                          <span className={classes.naText}>NA</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>

          <Box
            style={{
              padding: "16px 24px",
              display: "flex",
              gap: 25,
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Button
              variant="outlined"
              onClick={onClose}
              size="small"
              style={{
                flex: 1,
                textTransform: "none",
                borderColor: "#e0e0e0",
                color: "#666",
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => setConfirmOpen(true)}
              disabled={!canConfirm}
              style={{
                flex: 1,
                textTransform: "none",
                backgroundColor: !canConfirm ? undefined : "#1976d2",
              }}
            >
              Confirm changes
            </Button>
          </Box>
        </div>
      </Drawer>

      {/* Confirmation dialog */}
      <Dialog open={confirmOpen} onClose={() => !submitting && setConfirmOpen(false)}>
        <DialogTitle>Delete Plant ID {plantID}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will reassign {totalChanged} product
            {totalChanged === 1 ? "" : "s"} to the selected alternate plant
            {totalChanged === 1 ? "" : "s"} and remove Plant ID {plantID} from
            the network. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmOpen(false)}
            disabled={submitting}
            style={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="primary"
            variant="contained"
            disabled={submitting}
            style={{ textTransform: "none" }}
          >
            {submitting ? "Deleting..." : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success dialog — matches the BCM-234 design:
            "Delete Plant ID" title
            "Plant ID Deleted Successfully ✅"
            "Assigned Alternate Plant ID to"
            "<reassignedCount> Products"   (count from backend) */}
      <Dialog open={successOpen} onClose={handleSuccessOk} maxWidth="sm" fullWidth>
        <DialogTitle style={{ fontWeight: 700, paddingBottom: 8 }}>
          Delete Plant ID
        </DialogTitle>
        <Divider />
        <DialogContent style={{ paddingTop: 16, paddingBottom: 24 }}>
          <div className={classes.successHeading}>
            Plant ID Deleted Successfully
            <CheckCircleIcon className={classes.successCheck} />
          </div>
          <Typography className={classes.successSubLabel}>
            Assigned Alternate Plant ID to
          </Typography>
          <Typography className={classes.successCount}>
            {successCount} Product{successCount === 1 ? "" : "s"}
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: "8px 24px 16px" }}>
          <Button
            onClick={handleSuccessOk}
            color="primary"
            variant="contained"
            style={{ textTransform: "none", minWidth: 96 }}
          >
            Ok
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default DeletePlantDrawer;
